import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ConfigService } from '../services/config.service';
import { UserService } from '../services/user.service';
import { SearchService } from '../search/services/search.service';
import { formatString } from '../utils/string-utils';
import { SafeUrl } from '@angular/platform-browser';

@Injectable({
	providedIn: 'root'
})
export class ViewerService implements OnDestroy{
    safeViewerURL!: SafeUrl;
    private objectURL?: string;
    
	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private searchService: SearchService,
		private http: HttpClient,
		private sanitizer: DomSanitizer
	) { }
	ngOnDestroy(): void {
		if (this.objectURL) {
	      URL.revokeObjectURL(this.objectURL);
	    }
	}
	
	generateViewerURL(url?: string): SafeResourceUrl | undefined {
		if (!url) { return; }

		url += "?decache=" + Math.random();

		return this.sanitizer.bypassSecurityTrustResourceUrl(url);
	}	

	/** 
	 * Returns a documentObj with the document properties. 
	 */
	async processDocument(uuid: string | number, pageInfo: any, hitString: string | null): Promise<any> {
		const documentInfo = await this.getDocumentInfo(uuid, hitString);
		if (!documentInfo) {
			return Promise.reject("Failed to get document info.");
		}

		let documentObj = {
			documentID: uuid,
			title: '',
			index: false,
			status: false,
			count: 0,
			amount: 0,
			documentData: {},
			fullRecordMetadata: {},
			selectedPage: 0,
			pages: []
		};

		this.processDocumentInfoHelper(uuid, pageInfo, documentObj, documentInfo);

		const metadataRecord = await this.getMetadataRecord(uuid);
		if (!metadataRecord) {
			return Promise.reject("Failed to load product metadata.");
		}

		let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "IS_HIGHLIGHT_VIEW_FIELD" });
		if (property && property.value === "yes") {

			const highlightedMetadataRecord = await this.getHighlightedMetadataRecord(uuid);
			this.processMetadataHelper(documentObj, metadataRecord, highlightedMetadataRecord);

		} else {
			this.processMetadataHelper(documentObj, metadataRecord);
		}

		return Promise.resolve(documentObj);
	}

	private processDocumentInfoHelper(uuid: string | number, pageInfo: any, documentObj: any, documentData: any): void {
		let allPages = [];
		let amount = 0;
		let count = 0;
		let finalIndex = 0;
		let hasIndex = false;
		let hasStatus = false;
		let projected: { [key: string]: boolean } = {};

		for (let projectedPage of documentData.projectedPages) {
			projected[projectedPage] = true;
		}

		let slider = true;

		//we dont need to process at page level for PDF types.
		if (documentData.documentPages && (documentData.documentType != "application/pdf")) {
			for (let i = 0; i < documentData.documentPages.length; i++) {
				let page = documentData.documentPages[i];
				let pageNum = page.pageNum;

				if (page.sheet != null && page.sheet.length > 0) {
					for (let s = 0; s < page.sheet.length; s++) {
						let sheet = page.sheet[s];
						let pageType = 'sheet';
						let sIndex = sheet.sheetNum; //will have to be changed
						let title = "";
						if (page.type == "INDEX_PAGE") {
							title = "Index Page" + " SHT " + sIndex;
							pageType = "INDEX_PAGE";
							hasIndex = true;
						} else if (page.type == "STATUS_PAGE") {
							title = "Status Page" + " SHT " + sIndex;
							pageType = "STATUS_PAGE";
							hasStatus = true;
						} else {
							let title = 'PG ' + pageNum + " SHT " + sIndex;
						}
						allPages.push({
							productPageMetadata: page.productPageMetadata,
							fileType: page.fileType,
							type: pageType,
							index: s,
							secondaryNumber: sheet.sheetNum,
							pageNumber: pageNum,
							amount: sheet.amount,
							title: title,
							_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, uuid, pageNum, sIndex, "STANDARD"),
							_overview: formatString(this.configService.getConfig().document.overviewPageURL, uuid, pageNum, sIndex, "STANDARD")
						});
						amount += sheet.amount;
						if (pageInfo && pageInfo.type === "sheet" && pageInfo.pageNum === pageNum && pageInfo.secNum === sIndex) {
							finalIndex = count;
						}
						count++;
					}
				}

				if (page.overlay != null && page.overlay.length > 0) {
					for (let o = 0; o < page.overlay.length; o++) {
						let overlay = page.overlay[o];
						let oIndex = overlay.overlayNum;
						let title = "";
						if (page.type == "INDEX_PAGE") {
							title = "Index Page" + " OVRL " + oIndex;
						} else if (page.type == "STATUS_PAGE") {
							title = "Status Page" + " OVRL " + oIndex;
						} else {
							title = 'PG ' + pageNum + " OVRL " + oIndex;
						}
						allPages.push({
							productPageMetadata: page.productPageMetadata,
							fileType: page.fileType,
							type: "overlay",
							index: o,
							secondaryNumber: overlay.overlayNum,
							pageNumber: pageNum,
							amount: overlay.amount,
							title: title,
							_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, uuid, pageNum, oIndex, "OVERLAY"),
							_overview: formatString(this.configService.getConfig().document.overviewPageURL, uuid, pageNum, oIndex, "OVERLAY")
						});
						amount += overlay.amount;

						if (pageInfo && pageInfo.type === "overlay") {
							finalIndex = count;
						}

						count++;
					}
				}

				if ((page.overlay == null || page.overlay == 0) &&
					(page.sheet == null || page.sheet == 0) ||
					page.name &&
					page.name.toLowerCase().indexOf('ovrl') < 0 &&
					page.name.toLowerCase().indexOf('sht') < 0 &&
					page.name.toLowerCase().indexOf('_index_') < 0
				) {
					let title = "PG " + pageNum;
					if (page.type == "INDEX_PAGE") {
						hasIndex = true;
						title = "Index Page";
					}
					if (page.type == "STATUS_PAGE") {
						hasStatus = true;
						title = "Status Page";
					}
					allPages.push({
						productPageMetadata: page.productPageMetadata,
						fileType: page.fileType,
						projected: projected[pageNum],
						type: page.type,
						pageNumber: pageNum,
						amount: page.ammount,
						title: title,
						_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, uuid, pageNum, 0, "STANDARD"),
						_overview: formatString(this.configService.getConfig().document.overviewPageURL, uuid, pageNum, 0, "STANDARD")
					});
					amount += page.ammount;

					if (pageInfo && pageInfo.type === "standard" && pageInfo.pageNum === pageNum) {
						finalIndex = count;
					}
					count++;
				}
			}
		} else {
			let pages = documentData.documentPages ?? [];
			for (let i = 0; i < pages.length; i++) {
				let page = pages[i];
				let pageNum = page.pageNum;
				if (pageNum > 0) {
					amount += page.ammount;
					count++;
				}
			}
		}


		documentObj.index = hasIndex;
		documentObj.status = hasStatus;
		documentObj.count = count;
		documentObj.amount = amount;
		documentObj.documentData = documentData;
		documentObj.finalIndex = finalIndex;
		documentObj.pages = allPages;

		// TODO: loading full pdf by default now, load single page based on user prefs
		let currentPage = (documentData.pages > 0) ? 1 : 0;
		documentData.searchHitData = this.buildSearchHitData(currentPage, documentData.searchHitHighlights, 'Full');
		delete documentData.searchHitHighlights;

		if (documentObj.pages && documentObj.pages.length < 1 && documentData.projectedPages.length > 0) {
			documentObj.projected = true;
		}
	}

	private async getMetadataRecord(uuid: string | number): Promise<any> {
		const uuidStr = String(uuid);
		try {
			let metadata = await this.searchService.getRecord(uuidStr);
			return metadata;
		} catch (err) {
			console.error("Failed to get product metadata.", err);
			return Promise.reject(err);
		}
	}

	async getHighlightedMetadataRecord(uuid: string | number): Promise<any> {
		const uuidStr = String(uuid);
		let url = this.searchService.buildWS(uuidStr);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			console.error("Failed to fetch highlighted metadata record.", error);
			return Promise.reject(error);
		}
	}

	private processMetadataHelper(documentObj: any, metadataRecord: any, highlights?: any): void {
		let hl;
		if (!!(highlights && highlights.data && highlights.data["aw:record"] && highlights.data["aw:record"]["print"])) {
			hl = highlights.data["aw:record"]["print"]
		} else {
			hl = {};
		}

		let productHash = metadataRecord["csw:GetRecordByIdResponse"].iStoreRecord;
		let title = hl["title"] ? hl["title"] : productHash.title;
		title = this.searchService.trustHtml(title, false);

		documentObj.title = title;
		documentObj.fullRecordMetadata = productHash;
	}

	buildSearchHitData(currentPage: number, hitHighlights: any, docmode: string): any {
		let searchHitData = {
			pageNumber: 0,
			hasHit: false,
			hitIndex: [],
			closestHitIndex: {} as { previous: number; current: number; next: number; index: number },
			mode: docmode
		};

		searchHitData.hasHit = (hitHighlights && hitHighlights.length > 0) ? true : false;

		if (hitHighlights) {
			searchHitData.hitIndex = hitHighlights;
			searchHitData.closestHitIndex = this.findClosestHit(currentPage, hitHighlights, docmode);
		}

		searchHitData.pageNumber = currentPage;

		if (searchHitData.hasHit) {
			searchHitData.pageNumber = searchHitData.closestHitIndex.current || 0;
		}

		return searchHitData;
	}

	findClosestHit(target: number, hitarray: any[], docmode: string): any {
		let array = hitarray.map((num: any) => { return num * 1; });

		let current = target;
		if ('Full' == docmode) {
			target = target - 1;
			current = (target > -1) ? array[target] : 0;
		}

		let min = -1;
		for (let i = 0, len = array.length; i < len; i++) {
			if (array[i] == target || array[i] < target) {
				min = i;
			} else {
				break;
			}
		}

		let next = null;
		let nexttarget = min;
		if ('Full' == docmode) {
			nexttarget = target;
		}

		if ((nexttarget + 1) == array.length) {
			next = null;
		} else {
			next = array[nexttarget + 1];
		}

		let previous = null;
		if ((array[min]) < target) {
			previous = array[min];
		} else if (min - 1 > -1) {
			previous = array[min - 1];
		} else if (min - 1 < 0) {
			previous = null;
		}

		let indexes = { current: current, previous: previous, next: next, index: min };
		return indexes;
	}

	/**
	* Gets the KV annotation JSON model for any annotations;
	* that may exist on the pdf, such as when the pdf is highlighted with search hits.
	*/
	async getPdfAnnotationModel(prodid: number, searchString?: string): Promise<any> {
		let url = this.configService.getConfig().dx.baseAnnotationURL;
		url += `/annotation/${prodid}`;
		url += (searchString ? ("?searchString=" + searchString) : "");

		let annotationDTO = {
			state: "Redaction",
			mode: "Fill"
		};
		let payload = { annotationDTO };

		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});

		try {
			return await firstValueFrom(this.http.post<any>(url, payload, { headers }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getDocumentViewsInfo(uuid: string | number): Promise<any> {
		if (!this.configService.getConfig().viewer.docViewsEnabled) {
			return Promise.reject("Document views are not enabled.");
		}

		let url = formatString(this.configService.getConfig().document.documentViewsURL, uuid);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getDocumentInfo(uuid: string | number, hitString?: string | null): Promise<any> {
		let url = formatString(this.configService.getConfig().document.documentDescriberURL, uuid);

		if (!hitString) {
			try {
				return await firstValueFrom(this.http.get<any>(url, {}));
			} catch (error) {
				console.error("Document info call failed.", error);
				return Promise.reject(error);
			}
		}
		else {
			let searchString = hitString;
			if (this.searchService.type == '3' &&
				searchString === '*' &&
				this.searchService.criteria['lb_document_id'] &&
				this.searchService.criteria['lb_document_id'].content) {
				searchString = 'lb_document_id:' + this.searchService.criteria['lb_document_id'].content
			}

			let payload = formatString("qs=[{0}],qt=[{1}],qr=[],qf=[]", searchString, this.searchService.type);

			try {
				return await firstValueFrom(this.http.post<any>(url, payload));
			} catch (error) {
				console.error("Document info call failed.", error);
				return Promise.reject(error);
			}
		}
	}

	/* Returns the current zoom factor of the pdfjs object */
	getZoom(): string {
		let pdfhistory = localStorage["pdfjs.history"];
		if (!pdfhistory) return 'auto';
		let historyobj = JSON.parse(pdfhistory);
		let filehistory = historyobj.files;
		return filehistory[filehistory.length - 1].zoom;
	}

	buildDocIdSearchString(): string {
		let searchString = this.searchService.query || "";
		if (this.searchService.type == '3'
			&& searchString == '*'
			&& this.searchService.criteria['lb_document_id']
			&& this.searchService.criteria['lb_document_id'].content
		) {
			searchString = 'lb_document_id:' + this.searchService.criteria['lb_document_id'].content;
		}
		return encodeURIComponent(searchString) + ",qt=" + this.searchService.type;
	}

	buildHighlightSearchString(): string | undefined {
		let searchString = this.searchService.query || undefined;
		if (searchString) searchString = encodeURIComponent(searchString) + ",qt=" + this.searchService.type;
		return searchString;
	}

	// TODO Do we need this?
	async testSource(link: string): Promise<boolean> {
		try {
			if (this.objectURL) {
		      URL.revokeObjectURL(this.objectURL);
		    }
			const blob = await firstValueFrom(
		      this.http.get(link, { responseType: 'blob' })
		    );
		    const objectURL = URL.createObjectURL(blob);
            this.safeViewerURL = this.sanitizer.bypassSecurityTrustUrl(objectURL);

			/*await firstValueFrom(
				this.http.get(link, { observe: 'response', responseType: 'text' })
			);*/
			return true;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}
